import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { siteContent } from "../data/content";

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    country: "",
    email: "",
    whatsapp: "",
    product: "Cube 25mm",
    packaging: "",
    quantity: "",
    destination: "",
    notes: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build mailto body
    const body = `
Name: ${formData.name}
Company: ${formData.company}
Country: ${formData.country}
Email: ${formData.email}
WhatsApp: ${formData.whatsapp}
Product: ${formData.product}
Packaging Preference: ${formData.packaging}
Monthly Quantity: ${formData.quantity}
Destination Port/City: ${formData.destination}
Notes: ${formData.notes}
    `.trim();

    const subject = `Quote Request from ${formData.company || formData.name}`;
    const mailtoLink = `mailto:${siteContent.contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.location.href = mailtoLink;
  };

  return (
    <div>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-charcoal-paper border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
              Get In Touch
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6"
              data-testid="contact-title"
            >
              Contact / Request a Quote
            </h1>
            <p className="text-lg text-zinc-400">
              Fill out the form below or reach us directly via WhatsApp or email. 
              We typically respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">
                Contact Information
              </h2>
              
              <div className="space-y-6 mb-8">
                <a
                  href={`mailto:${siteContent.contact.email}`}
                  className="flex items-start gap-4 group"
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 bg-charcoal-paper border border-white/5 flex items-center justify-center flex-shrink-0 group-hover:border-coconut/30 transition-colors">
                    <Mail className="w-5 h-5 text-coconut" />
                  </div>
                  <div>
                    <div className="font-heading text-white uppercase text-sm mb-1">Email</div>
                    <div className="text-zinc-400 group-hover:text-coconut transition-colors">
                      {siteContent.contact.email}
                    </div>
                  </div>
                </a>

                <a
                  href={siteContent.contact.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                  data-testid="contact-whatsapp-link"
                >
                  <div className="w-12 h-12 bg-charcoal-paper border border-white/5 flex items-center justify-center flex-shrink-0 group-hover:border-[#25D366]/30 transition-colors">
                    <Phone className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div>
                    <div className="font-heading text-white uppercase text-sm mb-1">WhatsApp</div>
                    <div className="text-zinc-400 group-hover:text-[#25D366] transition-colors">
                      {siteContent.contact.whatsapp}
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-testid="contact-location">
                  <div className="w-12 h-12 bg-charcoal-paper border border-white/5 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-coconut" />
                  </div>
                  <div>
                    <div className="font-heading text-white uppercase text-sm mb-1">Location</div>
                    <div className="text-zinc-400" data-testid="contact-location-text">
                      {siteContent.contact.location}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick WhatsApp CTA */}
              <div className="bg-charcoal-paper border border-white/5 p-6">
                <h4 className="font-heading text-white uppercase text-sm mb-4">Prefer Instant Chat?</h4>
                <Button
                  asChild
                  className="w-full bg-[#25D366] text-white hover:bg-[#1da851] font-heading uppercase"
                  data-testid="contact-wa-quick-btn"
                >
                  <a
                    href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="mr-2 w-4 h-4" />
                    Chat on WhatsApp
                  </a>
                </Button>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="bg-charcoal-paper border border-white/5 p-8">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">
                  Request a Quote
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-zinc-300">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600"
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-zinc-300">Company *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600"
                        placeholder="Company name"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-zinc-300">Country *</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600"
                        placeholder="Your country"
                        data-testid="input-country"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-zinc-300">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp" className="text-zinc-300">WhatsApp Number</Label>
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600"
                        placeholder="+1 234 567 8900"
                        data-testid="input-whatsapp"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="product" className="text-zinc-300">Product</Label>
                      <Input
                        id="product"
                        name="product"
                        value={formData.product}
                        readOnly
                        className="bg-charcoal border-white/10 text-zinc-400"
                        data-testid="input-product"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Packaging Preference</Label>
                      <Select onValueChange={(value) => handleSelectChange("packaging", value)}>
                        <SelectTrigger className="bg-charcoal border-white/10 text-white" data-testid="select-packaging">
                          <SelectValue placeholder="Select packaging" />
                        </SelectTrigger>
                        <SelectContent className="bg-charcoal-paper border-white/10">
                          <SelectItem value="1kg-box">1kg Inner Box</SelectItem>
                          <SelectItem value="2kg-box">2kg Inner Box</SelectItem>
                          <SelectItem value="private-label">Private Label (Custom)</SelectItem>
                          <SelectItem value="bulk">Bulk (No Inner Box)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quantity" className="text-zinc-300">Quantity (Monthly)</Label>
                      <Input
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600"
                        placeholder="e.g., 1 container, 10 tons"
                        data-testid="input-quantity"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="destination" className="text-zinc-300">Destination Port/City</Label>
                    <Input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600"
                      placeholder="e.g., Dubai, Rotterdam"
                      data-testid="input-destination"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes" className="text-zinc-300">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      className="bg-charcoal border-white/10 text-white placeholder:text-zinc-600 resize-none"
                      placeholder="Any specific requirements, questions, or additional information..."
                      data-testid="input-notes"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase tracking-wider"
                    data-testid="submit-quote-btn"
                  >
                    <Send className="mr-2 w-4 h-4" />
                    Send Quote Request
                  </Button>

                  <p className="text-zinc-500 text-sm text-center">
                    Thanks for your interest — we'll reply within 24 hours.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
